<script lang="ts">
	import { afterNavigate, beforeNavigate } from '$app/navigation';
	import { page } from '$app/state';
	import { brandsSlugs } from '$lib';
	import CountrySelectorMobile from '$lib/components/CountrySelectorMobile.svelte';
	import { m } from '$lib/paraglide/messages';
	import { SvelteMap } from 'svelte/reactivity';
	import { slide } from 'svelte/transition';

	import DrawerButton from '../DrawerButton.svelte';

	const {
		menu,
		siblings,
		currentCountry,
	}: { menu: Menu[]; siblings: Config['siblings']; currentCountry: string } = $props();

	let isDrawerOpen = $state(false);
	const categoriesOpened = new SvelteMap<number, string>();

	function toggleCategory(depth: number, slug: string) {
		if (categoriesOpened.has(depth) && categoriesOpened.get(depth) === slug) {
			for (let i = depth; i < 2; i++) {
				categoriesOpened.delete(i);
			}
		} else {
			categoriesOpened.set(depth, slug);
		}
	}

	let oldDocumentOverflow = '';
	$effect(() => {
		if (isDrawerOpen) {
			oldDocumentOverflow = document.documentElement.style.overflow;
			document.documentElement.style.overflow = 'hidden';
		}

		return () => {
			if (isDrawerOpen) {
				document.documentElement.style.overflow = oldDocumentOverflow;
			}
		};
	});

	function resetCategoriesOpened() {
		categoriesOpened.clear();

		// Setup opened categories based on url
		menu.forEach((item: Menu) => {
			if (!item.children?.length || !page.url.pathname.startsWith('/' + item.slug)) {
				return;
			}
			categoriesOpened.set(0, item.slug);
			item.children.forEach((child: Menu) => {
				if (!child.children?.length || !page.url.pathname.startsWith('/' + child.slug)) {
					return;
				}

				categoriesOpened.set(1, child.slug);
			});
		});
	}

	beforeNavigate(() => {
		isDrawerOpen = false;
	});

	afterNavigate(() => {
		resetCategoriesOpened();
	});

	function getClassForMenuClass(
		menuClass: string | null,
		context: undefined | 'selected' | 'opened' = undefined,
	): string {
		switch (menuClass) {
			case 'sticky':
				if (context === 'selected') {
					return 'bg-neutral-800 text-white ring-2 ring-white';
				} else if (context === 'opened') {
					return 'bg-neutral-600 text-white';
				}

				return 'bg-neutral-800 text-white';

			case 'christmas':
				if (context === 'selected') {
					return 'bg-[#D4A50A] text-white ring-2 ring-white';
				} else if (context === 'opened') {
					return 'bg-[#A37F08] text-white';
				}

				return 'bg-[#D4A50A] text-white';

			default:
				if (context === 'selected') {
					return 'bg-neutral-800 text-white ';
				} else if (context === 'opened') {
					return 'bg-neutral-200 text-black';
				}

				return '';
		}
	}
</script>

{#snippet categoryMenu(category: Menu, depth = 0, parentSlug: string | undefined = undefined)}
	{@const isOpen =
		categoriesOpened.has(depth) &&
		categoriesOpened.get(depth) === category.slug &&
		category.children.length}
	{@const buttonClass = [
		'flex cursor-pointer px-6 transition-colors w-full items-center justify-between relative',
		getClassForMenuClass(category.menu_class),
		{ 'font-bold': depth === 0 || category.slug === parentSlug },
	]}
	<div class={['mx-6 border-neutral-200 not-first:border-t']}></div>
	{#if category.children.length}
		<button type="button" class={buttonClass} onclick={() => toggleCategory(depth, category.slug)}>
			<span
				class={[
					'my-1 flex w-full items-center justify-between rounded px-2 py-0.5 transition',
					{ 'font-bold': isOpen },
					isOpen ? getClassForMenuClass(category.menu_class, 'opened') : '',
				]}
			>
				{category.name}
				<span class={['icon-[mdi--chevron-down] transition', { 'rotate-180': isOpen }]}></span>
			</span>
		</button>
		{#if isOpen}
			<div transition:slide class="ml-10 h-full border-l border-neutral-200">
				{#each category.children.concat( [{ slug: category.slug, name: m.view_all_products(), children: [], is_button_menu: false, menu_class: null } as Menu], ) as subCategory (subCategory.slug)}
					{@render categoryMenu(subCategory, depth + 1, category.slug)}
				{/each}
			</div>
		{/if}
	{:else}
		<a class={buttonClass} href="/{category.slug}">
			<span
				class={[
					'my-1 flex w-full items-center justify-start rounded px-2 py-0.5 transition',
					{ 'font-bold': page.url.pathname === '/' + category.slug },
					page.url.pathname === '/' + category.slug
						? getClassForMenuClass(category.menu_class, 'selected')
						: '',
				]}
			>
				{category.name}</span
			>
		</a>
	{/if}
{/snippet}

<div class="block xl:hidden">
	<nav class="relative flex">
		<DrawerButton bind:isDrawerOpen class="size-12" />
	</nav>

	<!-- Grey overlay shown when the drawer is opened -->
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<!-- svelte-ignore a11y_click_events_have_key_events -->
	<div
		class={[
			'fixed top-[var(--header-height)] right-0 bottom-0 left-0 z-30 w-full duration-300',
			isDrawerOpen ? 'bg-black/70' : 'pointer-events-none bg-black/0',
		]}
		onclick={() => (isDrawerOpen = false)}
	></div>

	<div
		id="drawer-navigation"
		class={[
			'fixed left-0 z-40 w-[100vw] bg-transparent duration-300 md:w-80',
			'top-[var(--header-height)] max-h-[calc(100dvh-var(--header-height))]',
			isDrawerOpen ? 'translate-x-0 overflow-y-auto' : ' -translate-x-full overflow-y-hidden',
		]}
	>
		<div class="min-h-full select-none">
			<div class="bg-white">
				{#each menu as category (category.slug)}
					{@render categoryMenu(category)}
				{/each}

				<div class="flex cursor-pointer items-center justify-between px-8 py-2">
					<a href="/{brandsSlugs[page.data.store.language]}" class="font-bold">{m.brands()}</a>
				</div>
			</div>
			<div
				class="mt-0 grid grid-cols-[auto_1fr] items-center gap-6 bg-neutral-800 px-6
					py-8 font-semibold text-white uppercase transition-[margin] md:mt-8 md:rounded-r"
			>
				<CountrySelectorMobile {siblings} {currentCountry} />
				<span class="icon-[mdi--location] h-6 w-6"></span>
				<span class="text-xs text-nowrap uppercase">
					{m['reinsurances.after_sales_services']()}
				</span>
				<span class="icon-[mdi--credit-card-lock] h-6 w-6"></span>
				<span class="text-xs text-nowrap uppercase">
					{m['reinsurances.safe_payment']()}
				</span>
				<span class="text-lg font-bold">4x</span>
				<span class="text-xs text-nowrap uppercase">
					{m['reinsurances.free_of_charge']()}
				</span>
				<span class="icon-[mdi--truck-fast] h-6 w-6"></span>
				<span class="text-xs text-nowrap uppercase">
					{m['reinsurances.easy_returns']()}
				</span>
			</div>
		</div>
	</div>
</div>
